/**
 * Rooms: the units a sheet boundary never divides. Each room tag floods outward until it meets
 * a wall or a door; whatever no tag claims is circulation, which the boundary may cross.
 */

import type { Mask } from "./grid.ts";
import { close } from "./grid.ts";
import type { Footprint, Raster } from "./footprint.ts";
import { cellRadius, rasterSegments } from "./footprint.ts";
import type { Segment } from "./input.ts";

export interface Room {
  readonly id: number;
  readonly building: number;
  readonly cells: Int32Array;
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly centroidX: number;
  readonly centroidY: number;
  /** True when the flood escaped and only the area around the tag was kept. */
  readonly leaked: boolean;
}

export interface RoomOptions {
  /** Openings narrower than twice this are sealed even without a detected door. */
  readonly sealRadiusInches: number;
  /** A flood larger than this is a leak into circulation, not a room. */
  readonly maxRoomSquareInches: number;
  /** What a leaked tag keeps: everything within this geodesic distance. */
  readonly leakedRoomRadiusInches: number;
}

export interface Rooms {
  /** Room id per cell, -1 for circulation and outside. */
  readonly room: Int32Array;
  readonly rooms: readonly Room[];
  readonly leaked: number;
}

function nearestOpen(barrier: Mask, width: number, start: number, reach: number): number {
  if (barrier[start] === 0) return start;
  const sx = start % width;
  const sy = Math.floor(start / width);
  for (let r = 1; r <= reach; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = sx + dx;
        const y = sy + dy;
        if (x < 0 || y < 0 || x >= width) continue;
        const i = y * width + x;
        if (i < barrier.length && barrier[i] === 0) return i;
      }
    }
  }
  return -1;
}

export function findRooms(
  raster: Raster,
  footprint: Footprint,
  doorSeals: readonly Segment[],
  tagCells: readonly number[],
  options: RoomOptions,
): Rooms {
  const { width } = raster.shape;
  const sealed = rasterSegments(raster, doorSeals, footprint.walls.slice());
  const barrier = close(sealed, raster.shape, cellRadius(raster, options.sealRadiusInches));
  const room = new Int32Array(barrier.length).fill(-1);
  const rooms: Room[] = [];
  const maxCells = options.maxRoomSquareInches / (raster.cell * raster.cell);
  const leakRadius = cellRadius(raster, options.leakedRoomRadiusInches);
  const queue = new Int32Array(barrier.length);
  const depth = new Int32Array(barrier.length);
  const seen = new Int32Array(barrier.length).fill(-1);
  let leakedCount = 0;

  const flood = (start: number, stamp: number, limit: number): { cells: Int32Array; leaked: boolean } => {
    let tail = 0;
    let leaked = false;
    seen[start] = stamp;
    depth[start] = 0;
    queue[tail++] = start;
    for (let head = 0; head < tail; head++) {
      const i = queue[head]!;
      if (footprint.mask[i] === 0) leaked = true;
      if (tail > maxCells) leaked = true;
      if (leaked && limit === Infinity) break;
      if (depth[i]! >= limit) continue;
      const x = i % width;
      const visit = (n: number) => {
        if (barrier[n] === 0 && seen[n] !== stamp && room[n] === -1) {
          seen[n] = stamp;
          depth[n] = depth[i]! + 1;
          queue[tail++] = n;
        }
      };
      if (x > 0) visit(i - 1);
      if (x < width - 1) visit(i + 1);
      if (i >= width) visit(i - width);
      if (i < barrier.length - width) visit(i + width);
    }
    return { cells: queue.slice(0, tail), leaked };
  };

  let stamp = 0;
  for (const tagCell of tagCells) {
    const start = nearestOpen(barrier, width, tagCell, cellRadius(raster, 24));
    if (start < 0 || room[start] !== -1) continue;
    const building = footprint.building[start]!;
    if (building < 0) continue;

    let result = flood(start, stamp++, Infinity);
    let leaked = false;
    if (result.leaked) {
      leaked = true;
      leakedCount++;
      result = flood(start, stamp++, leakRadius);
    }

    const id = rooms.length;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let sumX = 0;
    let sumY = 0;
    const cells = result.cells.filter((i) => footprint.building[i] === building);
    for (const i of cells) {
      room[i] = id;
      const x = i % width;
      const y = Math.floor(i / width);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
      sumX += x;
      sumY += y;
    }
    if (cells.length === 0) continue;
    rooms.push({
      id,
      building,
      cells,
      minX,
      minY,
      maxX,
      maxY,
      centroidX: sumX / cells.length,
      centroidY: sumY / cells.length,
      leaked,
    });
  }
  return { room, rooms, leaked: leakedCount };
}
